import { useEffect, useId, useState } from "react";

import { useToast } from "@/ds";
import { useUserPhoto } from "@/hooks";
import { formatUserPhone } from "@/modules/users";
import { getUserPhotoPathByEmail } from "@/shared/utils/user-photo";

import {
  AVATAR_CLASS_BY_SIZE,
  COPIED_FEEDBACK_MS,
  COPY_EMAIL_COPIED_TOOLTIP,
  COPY_EMAIL_ERROR_DETAIL,
  COPY_EMAIL_ERROR_SUMMARY,
  COPY_EMAIL_TOOLTIP,
} from "./user-contact-card.constants";
import type { UserContactCardProps, UserContactCardViewModel } from "./types-user-contact-card";

/**
 * Dados do cartão: telefone formatado, foto (por `fotoPath` ou pelo e-mail, se o consumidor não a trouxe em `fotoUrl`) e o
 * copiar do e-mail: o sucesso vira "Copiado" no tooltip por um instante; a falha avisa por toast em vez de ser engolida.
 */
export function useUserContactCard({
  titulo,
  nome,
  email,
  telefone,
  detalhe,
  fotoUrl,
  fotoPath,
  buscarFoto = true,
  avatarSize = "md",
  testIds,
  className,
  ...rest
}: UserContactCardProps): UserContactCardViewModel {
  const toast = useToast();
  const copyId = `user-contact-copy-${useId().replace(/:/g, "")}`;
  const emailLimpo = email?.trim() ?? "";
  const fetchedPhoto = useUserPhoto(
    fotoUrl ? null : (fotoPath ?? getUserPhotoPathByEmail(emailLimpo)),
    buscarFoto,
  );

  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;

    const timer = setTimeout(() => setCopiado(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copiado]);

  async function copiarEmail() {
    if (!emailLimpo) return;

    try {
      await navigator.clipboard.writeText(emailLimpo);
      setCopiado(true);
    } catch {
      toast.error(COPY_EMAIL_ERROR_SUMMARY, COPY_EMAIL_ERROR_DETAIL);
    }
  }

  return {
    rest,
    className,
    titulo: titulo ?? "",
    nome,
    email: emailLimpo,
    telefone: telefone ? formatUserPhone(telefone) : "",
    detalhe: detalhe?.trim() ?? "",
    fotoSrc: fotoUrl || fetchedPhoto,
    avatarClassName: AVATAR_CLASS_BY_SIZE[avatarSize],
    testIds,
    copy: {
      id: copyId,
      label: `Copiar e-mail de ${nome}`,
      tooltip: copiado ? COPY_EMAIL_COPIED_TOOLTIP : COPY_EMAIL_TOOLTIP,
    },
    copiarEmail,
  };
}
